import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import { getContacts, createContact, updateContact, deleteContact } from '../api/crmApi';
import type { ContactFilters, CreateContactInput } from '../types';

const STALE_TIME = 2 * 60 * 1000; // 2 minutes

export const crmContactKeys = {
  all: ['crm-contacts'] as const,
  list: (
    filters?: ContactFilters,
    page?: number,
    sortBy?: string,
    sortDirection?: 1 | -1,
  ) => [...crmContactKeys.all, 'list', filters, page, sortBy, sortDirection] as const,
};

export function useContacts(
  filters?: ContactFilters,
  page = 0,
  sortBy?: string,
  sortDirection?: 1 | -1,
) {
  return useQuery({
    queryKey: crmContactKeys.list(filters, page, sortBy, sortDirection),
    queryFn: () => getContacts(filters, page, 25, sortBy, sortDirection),
    staleTime: STALE_TIME,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContactInput) => createContact(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmContactKeys.all });
      toast.success(i18next.t('crm:contacts.toast.created'));
    },
    onError: (error: Error) => {
      toast.error(i18next.t('crm:contacts.toast.failedCreate'), { description: error.message });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<CreateContactInput> & { id: string }) =>
      updateContact(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmContactKeys.all });
      toast.success(i18next.t('crm:contacts.toast.updated'));
    },
    onError: (error: Error) => {
      toast.error(i18next.t('crm:contacts.toast.failedUpdate'), { description: error.message });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmContactKeys.all });
      toast.success(i18next.t('crm:contacts.toast.deleted'));
    },
    onError: (error: Error) => {
      toast.error(i18next.t('crm:contacts.toast.failedDelete'), { description: error.message });
    },
  });
}
